import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { KitAssetTrim } from 'fym-shared/src/music24/fullVideoPlan';
import { TypedMemo } from '../../../../common/types';
import { canTrimSong, clockTime, defaultTrim } from '../fullVideoRules';
import { fullVideoMessages } from '../fullVideoMessages';
import { studioMessages } from '../studioMessages';
import { DetailField } from './DetailField';
import { ChoiceChip, ChoiceChips } from './ChoiceChips';
import { TrimFields } from './TrimFields';

type LengthMode = 'whole' | 'trim';

interface FullVideoLengthFieldProps {
  durationSec: number | null;
  trim?: KitAssetTrim;
  /** Undefined: the whole song. */
  onChange: (trim: KitAssetTrim | undefined) => void;
}

/**
 * "Length" of a full video (design s7): "Whole song · 3:54" sends no trim; "Trim" shows the two
 * time fields. The trim starts as the whole song, so a trim that cuts nothing sends no trim either.
 */
const FullVideoLengthFieldComponent: React.FC<FullVideoLengthFieldProps> = ({ durationSec, trim, onChange }) => {
  const intl = useIntl();
  const title = intl.formatMessage(studioMessages.lengthTitle);
  const canTrim = canTrimSong(durationSec);
  const options = useMemo<Array<ChoiceChip<LengthMode>>>(
    () => [
      {
        value: 'whole',
        label:
          durationSec === null
            ? intl.formatMessage(fullVideoMessages.lengthWholeNoTime)
            : intl.formatMessage(fullVideoMessages.lengthWhole, { time: clockTime(durationSec) }),
      },
      { value: 'trim', label: intl.formatMessage(fullVideoMessages.lengthTrim), disabled: !canTrim },
    ],
    [canTrim, durationSec, intl]
  );

  const pick = (mode: LengthMode) => {
    if (mode === 'whole' || durationSec === null) {
      onChange(undefined);
      return;
    }
    onChange(trim ?? defaultTrim(durationSec));
  };

  return (
    <DetailField title={title} hint={intl.formatMessage(fullVideoMessages.lengthHint)}>
      <ChoiceChips label={title} options={options} value={trim === undefined ? 'whole' : 'trim'} onChange={pick} />
      <span className="small text-muted">{intl.formatMessage(fullVideoMessages.trimNote)}</span>
      {trim !== undefined && durationSec !== null && (
        <TrimFields durationSec={durationSec} trim={trim} onChange={onChange} />
      )}
    </DetailField>
  );
};

export const FullVideoLengthField = TypedMemo(FullVideoLengthFieldComponent);
